import type { CategoryNode, Service } from "../../../types";

/** Raw shape returned by GET /api/service-categories. */
export interface RawCategory {
  id: string;
  key: string;
  name: string;
  nameEt?: string | null;
  nameEn?: string | null;
  nameRu?: string | null;
  displayName?: string;
  icon: string;
  sortOrder?: number;
  parentId?: string | null;
  isActive?: boolean;
  /** True when this is an orderable leaf (no children in the DB, even inactive). */
  isLeaf?: boolean;
}

/**
 * Canonical mapper from an API category row to the frontend `Service` shape.
 * `Service.id` is the category KEY (the value used in orders & provider.services);
 * the hierarchy/localization fields are preserved so nothing is lost downstream.
 *
 * IMPORTANT: every surface that consumes /api/service-categories must use this
 * instead of an inline `.map`, or it silently drops parentId / localized names.
 */
export function mapCategory(raw: RawCategory): Service {
  return {
    id: raw.key,
    key: raw.key,
    name: raw.name,
    icon: raw.icon,
    parentId: raw.parentId ?? null,
    nameEt: raw.nameEt ?? null,
    nameEn: raw.nameEn ?? null,
    nameRu: raw.nameRu ?? null,
    isActive: raw.isActive ?? true,
  };
}

/**
 * Build an ordered list of top-level nodes from raw API rows. Children are
 * grouped under their parent (matched by the parent's real DB id). Standalone
 * leaves (no parent, no children) become leaf nodes.
 *
 * By default inactive categories are filtered out (end-user surfaces); pass
 * `includeInactive` for the admin view. A group with zero (visible) children is
 * omitted on end-user surfaces.
 */
export function buildCategoryTree(
  raw: RawCategory[],
  opts: { includeInactive?: boolean } = {}
): CategoryNode[] {
  const includeInactive = opts.includeInactive ?? false;
  const rows = includeInactive ? raw : raw.filter((r) => r.isActive !== false);

  const childrenByParent = new Map<string, RawCategory[]>();
  for (const r of rows) {
    if (r.parentId) {
      const list = childrenByParent.get(r.parentId) ?? [];
      list.push(r);
      childrenByParent.set(r.parentId, list);
    }
  }

  const sortBySortOrder = (a: RawCategory, b: RawCategory) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

  const nodes: CategoryNode[] = [];
  for (const r of [...rows].sort(sortBySortOrder)) {
    if (r.parentId) continue; // handled under its parent
    const kids = (childrenByParent.get(r.id) ?? []).sort(sortBySortOrder);
    // A grouping header has children in the DB. `isLeaf === false` covers the
    // case where its children are all inactive (and thus absent from `raw`).
    const isGroupingHeader = raw.some((c) => c.parentId === r.id) || r.isLeaf === false;

    if (kids.length > 0) {
      nodes.push({ category: mapCategory(r), children: kids.map(mapCategory), isGroup: true });
    } else if (isGroupingHeader) {
      // A grouping header with no visible children — skip on end-user surfaces,
      // keep (as empty group) for admin.
      if (includeInactive) {
        nodes.push({ category: mapCategory(r), children: [], isGroup: true });
      }
    } else {
      nodes.push({ category: mapCategory(r), children: [], isGroup: false });
    }
  }
  return nodes;
}

/** Leaf keys reachable from a node (the node itself if a leaf, else its children). */
export function getLeafKeys(node: CategoryNode): string[] {
  return node.isGroup ? node.children.map((c) => c.id) : [node.category.id];
}
